import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { landlordAPI, getUploadUrl } from '../../lib/api';
import { getInitials } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Loader2, UserPlus, Send, Search, XCircle, ChevronRight, Archive, Star
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRoleScope } from '../../hooks/useRoleScope';
import TenantOffboardDialog from '../../components/landlord/TenantOffboardDialog';
import TenantHistoryList from '../../components/landlord/TenantHistoryList';
import { TenantDetailPanel, getStatusBadge } from '../../components/landlord/tenants/TenantDetailPanel';
import { EditTenantDialog } from '../../components/landlord/tenants/EditTenantDialog';
import { AddTenantDialog } from '../../components/landlord/tenants/AddTenantDialog';
import { RecordPaymentDialog } from '../../components/landlord/tenants/RecordPaymentDialog';

export default function LandlordTenants() {
  const { t } = useLanguage();
  const { isManager, can, basePath } = useRoleScope();
  const navigate = useNavigate();

  const [tenants, setTenants] = useState([]);
  const [pendingTenants, setPendingTenants] = useState([]);
  const [invitedTenants, setInvitedTenants] = useState([]);
  const [manualTenants, setManualTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search + filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Selected tenant for side panel
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);

  // Dialogs
  const [addTenantDialogOpen, setAddTenantDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  // Offboarding + history
  const [offboardDialogOpen, setOffboardDialogOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [reonboardingId, setReonboardingId] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tenantsRes, pendingRes, invitedRes, manualRes, propsRes, unitsRes] = await Promise.all([
        landlordAPI.getTenants().catch(() => ({ data: [] })),
        landlordAPI.getPendingTenants().catch(() => ({ data: [] })),
        landlordAPI.getPendingTenantsInvited().catch(() => ({ data: [] })),
        landlordAPI.getManualTenants().catch(() => ({ data: [] })),
        landlordAPI.getProperties().catch(() => ({ data: [] })),
        landlordAPI.getUnits().catch(() => ({ data: [] }))
      ]);
      setTenants(tenantsRes.data);
      setPendingTenants(pendingRes.data || []);
      setInvitedTenants(invitedRes.data || []);
      setManualTenants(manualRes.data || []);
      setProperties(propsRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  // Build unified tenant list
  const allTenants = useMemo(() => {
    const list = [
      ...tenants.filter(t => t.active_lease && !t.is_manual_tenant).map(t => ({ ...t, _type: 'active', _status: 'Active Lease' })),
      ...tenants.filter(t => !t.active_lease && t.has_pending_lease && !t.is_manual_tenant).map(t => ({ ...t, _type: 'pending_lease', _status: 'Pending Lease' })),
      // Self-registered tenants awaiting landlord verification
      ...tenants.filter(t => !t.active_lease && !t.has_pending_lease && !t.is_manual_tenant && t.is_verified === false).map(t => ({ ...t, _type: 'pending', _status: 'Pending Verification' })),
      ...pendingTenants.map(t => ({ ...t, _type: 'pending', _status: 'Pending Verification', name: t.name || t.tenant_name })),
      ...invitedTenants.map(t => ({ ...t, _type: 'invited', _status: 'Invited', name: t.name || t.tenant_name, email: t.email || t.tenant_email, phone: t.phone || t.tenant_phone })),
      ...tenants.filter(t => !t.active_lease && !t.has_pending_lease && !t.had_previous_lease && !t.is_manual_tenant && t.is_verified !== false).map(t => ({ ...t, _type: 'available', _status: 'Available' })),
      ...tenants.filter(t => !t.active_lease && !t.has_pending_lease && t.had_previous_lease && !t.is_manual_tenant).map(t => ({ ...t, _type: 'former', _status: 'Former' })),
      ...manualTenants.filter(m => m.is_active !== false && !m.merged_into).map(t => ({ ...t, _type: 'manual', _status: 'Manual Entry' })),
    ];
    // Deduplicate by user_id/pending_tenant_id and by email
    const seenIds = new Set();
    const seenEmails = new Set();
    const deduped = list.filter(t => {
      const id = t.user_id || t.pending_tenant_id;
      const email = (t.email || t.tenant_email || '').toLowerCase().trim();
      if (id && seenIds.has(id)) return false;
      if (email && seenEmails.has(email)) return false;
      if (id) seenIds.add(id);
      if (email) seenEmails.add(email);
      return true;
    });
    deduped.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return deduped;
  }, [tenants, pendingTenants, invitedTenants, manualTenants]);

  const filteredTenants = useMemo(() => {
    let result = allTenants;
    if (filterStatus !== 'all') {
      result = result.filter(t => t._type === filterStatus);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t =>
        (t.name || '').toLowerCase().includes(q) ||
        (t.email || '').toLowerCase().includes(q) ||
        (t.phone || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [allTenants, searchQuery, filterStatus]);

  const statusCounts = useMemo(() => {
    const counts = { all: allTenants.length, active: 0, pending_lease: 0, manual: 0, invited: 0, pending: 0, available: 0, former: 0 };
    allTenants.forEach(t => { if (counts[t._type] !== undefined) counts[t._type]++; });
    return counts;
  }, [allTenants]);

  const openPanel = (tenant) => {
    setSelectedTenant(tenant);
    setSelectedType(tenant._type);
    setPanelOpen(true);
  };

  const getTenantId = () => selectedTenant?.user_id || selectedTenant?.pending_tenant_id || '';
  const getTenantEmail = () => selectedTenant?.email || selectedTenant?.tenant_email || '';
  const getTenantName = () => selectedTenant?.name || selectedTenant?.tenant_name || '';

  const getAvatarUrl = (tenant) => {
    if (!tenant?.avatar) return null;
    if (tenant.avatar.startsWith('http')) return tenant.avatar;
    return getUploadUrl(tenant.avatar);
  };

  const getPropertyName = (propId) => properties.find(p => p.property_id === propId)?.name || '';
  const getUnitName = (unitId) => units.find(u => u.unit_id === unitId)?.unit_number || '';

  // ===== History =====
  const loadHistory = async () => {
    try {
      const { data } = await landlordAPI.getTenantHistory();
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoaded(true);
    }
  };

  useEffect(() => {
    if (filterStatus === 'history' && !historyLoaded) {
      loadHistory();
    }
  }, [filterStatus, historyLoaded]);

  const handleOffboardSuccess = () => {
    setHistoryLoaded(false);
    if (filterStatus === 'history') {
      loadHistory();
    }
    setPanelOpen(false);
    loadData();
  };

  const handleDownloadMoveOutStatement = async (h) => {
    setDownloadingId(h.history_id);
    try {
      const response = await landlordAPI.downloadMoveOutStatement(h.history_id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (h.tenant_name || 'tenant').replace(/\s+/g, '_');
      a.download = `move_out_statement_${safeName}_${h.move_out_date || 'undated'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Move-out statement downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download statement');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleReonboard = async (h) => {
    if (!window.confirm(`Reactivate ${h.tenant_name}'s account so you can create a new lease for them?`)) return;
    setReonboardingId(h.history_id);
    try {
      const { data } = await landlordAPI.reonboardTenant(h.history_id);
      toast.success(
        data.email_sent
          ? `${data.tenant_name} reactivated — welcome-back email sent ✓`
          : `${data.tenant_name} reactivated`
      );
      navigate(`${basePath}/leases?newLease=true&tenant=${data.tenant_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to re-onboard tenant');
    } finally {
      setReonboardingId(null);
    }
  };

  // ===== Panel actions =====
  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.body) { toast.error('Subject and body are required'); return; }
    setSendingEmail(true);
    try {
      await landlordAPI.sendEmailToTenant({
        tenant_id: getTenantId(),
        to_email: getTenantEmail(),
        subject: emailForm.subject,
        body: emailForm.body
      });
      toast.success('Email sent');
      setEmailDialogOpen(false);
      setEmailForm({ subject: '', body: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally { setSendingEmail(false); }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) { toast.error('Email is required'); return; }
    setSendingInvite(true);
    try {
      if (selectedType === 'manual') {
        await landlordAPI.convertManualTenant(getTenantId(), inviteEmail);
        toast.success('Invitation sent! Tenant moved to Invited.');
      } else if (selectedType === 'invited') {
        await landlordAPI.resendPendingTenantInvite(selectedTenant.pending_tenant_id);
        toast.success('Invitation resent!');
      } else {
        await landlordAPI.invitePendingTenant({ name: getTenantName(), email: inviteEmail, phone: selectedTenant?.phone || '' });
        toast.success('Invitation sent!');
      }
      setInviteDialogOpen(false);
      setInviteEmail('');
      loadData();
      setPanelOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally { setSendingInvite(false); }
  };

  const handleRemoveTenant = async () => {
    if (!window.confirm(`Remove ${getTenantName()}? This action cannot be undone.`)) return;
    try {
      if (selectedType === 'invited') {
        await landlordAPI.deletePendingTenant(selectedTenant.pending_tenant_id);
      } else {
        await landlordAPI.removeTenant(getTenantId());
      }
      toast.success('Tenant removed');
      setPanelOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove tenant');
    }
  };

  const handleVerifyTenant = async () => {
    if (!window.confirm(`Approve ${getTenantName()}? They will be able to access the tenant portal.`)) return;
    setVerifying(true);
    try {
      await landlordAPI.verifyTenant(getTenantId());
      toast.success(`${getTenantName()} has been approved!`);
      setPanelOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify tenant');
    } finally { setVerifying(false); }
  };

  const handleRejectTenant = async () => {
    if (!window.confirm(`Reject ${getTenantName()}? Their registration will be deleted.`)) return;
    setRejecting(true);
    try {
      await landlordAPI.rejectTenant(getTenantId());
      toast.success(`${getTenantName()} has been rejected.`);
      setPanelOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject tenant');
    } finally { setRejecting(false); }
  };

  const handleEditSaved = (payload) => {
    loadData();
    setSelectedTenant(prev => ({ ...prev, ...payload }));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="landlord-tenants-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">{t('tenants.title')}</h1>
          <p className="text-muted-foreground mt-1">{allTenants.length} total tenants</p>
        </div>
        <Button onClick={() => setAddTenantDialogOpen(true)} className="bg-[#C85A42] hover:bg-[#B54A35]" data-testid="add-tenant-btn" disabled={isManager && !can('invite_tenants')} style={{ display: isManager && !can('invite_tenants') ? 'none' : undefined }}>
          <UserPlus className="w-4 h-4 mr-2" /> Add Tenant
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Find tenant by name, email, or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="tenant-search-input"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <XCircle className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2" data-testid="tenant-status-filters">
        {[
          { key: 'all', label: 'All' },
          { key: 'active', label: 'Active Lease' },
          { key: 'pending_lease', label: 'Pending Lease' },
          { key: 'manual', label: 'Manual Entry' },
          { key: 'invited', label: 'Invited' },
          { key: 'pending', label: 'Pending Verification' },
          { key: 'available', label: 'Available' },
          { key: 'former', label: 'Former' },
          { key: 'history', label: 'History', forceShow: true },
        ].filter(f => f.forceShow || f.key === 'all' || statusCounts[f.key] > 0).map(f => (
          <button
            key={f.key}
            onClick={() => setFilterStatus(f.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filterStatus === f.key
                ? 'bg-[#2F4F4F] text-white'
                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
            data-testid={`tenant-filter-${f.key}`}
          >
            {f.key === 'history' && <Archive className="w-3.5 h-3.5" />}
            {f.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              filterStatus === f.key ? 'bg-white/20' : 'bg-background'
            }`}>
              {f.key === 'history' ? history.length : statusCounts[f.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Tenant List */}
      {filterStatus === 'history' ? (
        <TenantHistoryList
          history={history}
          loaded={historyLoaded}
          downloadingId={downloadingId}
          reonboardingId={reonboardingId}
          onDownload={handleDownloadMoveOutStatement}
          onReonboard={handleReonboard}
          canReonboard={!isManager || can('invite_tenants')}
        />
      ) : filteredTenants.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg">{searchQuery || filterStatus !== 'all' ? 'No tenants match your filters' : 'No tenants yet'}</h3>
            <p className="text-muted-foreground mt-2">
              {searchQuery || filterStatus !== 'all' ? 'Try adjusting your search or filter' : 'Click "Add Tenant" to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {filteredTenants.map((tenant) => {
                const id = tenant.user_id || tenant.pending_tenant_id;
                const uniqueKey = `${tenant._type}-${id}`;
                return (
                  <div
                    key={uniqueKey}
                    className="flex items-center gap-4 p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => openPanel(tenant)}
                    data-testid={`tenant-row-${id}`}
                  >
                    <Avatar className="h-10 w-10 shrink-0">
                      {getAvatarUrl(tenant) ? (
                        <AvatarImage src={getAvatarUrl(tenant)} alt={tenant.name} />
                      ) : null}
                      <AvatarFallback className="bg-[#2F4F4F] text-white text-sm">
                        {getInitials(tenant.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-medium truncate">{tenant.name || 'N/A'}</p>
                        {tenant.is_repeat_tenant && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800"
                            title="This tenant has rented from you before."
                            data-testid={`repeat-tenant-badge-${id}`}
                          >
                            <Star className="w-2.5 h-2.5 fill-current" />
                            Repeat
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {tenant.email || tenant.tenant_email || tenant.phone || tenant.tenant_phone || 'No contact info'}
                      </p>
                    </div>
                    <div className="hidden md:flex items-center gap-2">
                      {(() => {
                        const lease = tenant.active_lease || tenant.has_pending_lease;
                        const propName = tenant.current_property?.name || (lease?.property_id && getPropertyName(lease.property_id)) || (tenant.property_id && getPropertyName(tenant.property_id));
                        const unitName = tenant.current_unit?.unit_number || (lease?.unit_id && getUnitName(lease.unit_id)) || (tenant.unit_id && getUnitName(tenant.unit_id));
                        return (
                          <>
                            {propName && <span className="text-xs text-muted-foreground">{propName}</span>}
                            {unitName && <span className="text-xs text-muted-foreground">Room {unitName}</span>}
                          </>
                        );
                      })()}
                    </div>
                    <Badge className={getStatusBadge(tenant._type)}>{tenant._status}</Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tenant detail side panel */}
      <TenantDetailPanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        tenant={selectedTenant}
        selectedType={selectedType}
        isManager={isManager}
        can={can}
        basePath={basePath}
        getPropertyName={getPropertyName}
        getUnitName={getUnitName}
        onTenantUpdated={setSelectedTenant}
        onDataChanged={loadData}
        onEdit={() => setEditDialogOpen(true)}
        onEmail={() => setEmailDialogOpen(true)}
        onInvite={(prefillEmail) => { setInviteEmail(prefillEmail || ''); setInviteDialogOpen(true); }}
        onPayment={() => setPaymentDialogOpen(true)}
        onOffboard={() => setOffboardDialogOpen(true)}
        onRemove={handleRemoveTenant}
        onVerify={handleVerifyTenant}
        onReject={handleRejectTenant}
        verifying={verifying}
        rejecting={rejecting}
      />

      {/* Dialogs */}
      <AddTenantDialog
        open={addTenantDialogOpen}
        onOpenChange={setAddTenantDialogOpen}
        onSuccess={loadData}
      />

      <EditTenantDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        tenant={selectedTenant}
        selectedType={selectedType}
        onSaved={handleEditSaved}
      />

      <RecordPaymentDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        tenantId={getTenantId()}
        tenantType={selectedType === 'invited' ? 'pending' : 'active'}
        tenantName={getTenantName()}
      />

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription>Send to: {getTenantEmail()}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} placeholder="Email subject" data-testid="email-subject" />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea value={emailForm.body} onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} rows={5} placeholder="Type your message..." data-testid="email-body" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSendEmail} disabled={sendingEmail} className="bg-[#C85A42] hover:bg-[#B54A35]" data-testid="send-email-btn">
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Send Invitation</DialogTitle>
            <DialogDescription>Invite {getTenantName()} to use the Tenant Portal</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="tenant@email.com" data-testid="invite-email" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSendInvite} disabled={sendingInvite} className="bg-[#C85A42] hover:bg-[#B54A35]" data-testid="send-invite-btn">
                {sendingInvite ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Invite
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <TenantOffboardDialog
        open={offboardDialogOpen}
        onOpenChange={setOffboardDialogOpen}
        tenant={selectedTenant}
        onSuccess={handleOffboardSuccess}
      />
    </div>
  );
}
